import {
  createRootRoute,
  createRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useLayoutEffect, useState } from "react";
import { AppShell, filterMenu, MENU, type RenderLink } from "@cx/ui";
import { can, type Permissions } from "@cx/db";
import { useSession } from "./session";
import { applyTheme, readTheme, saveTheme, type Theme } from "./theme";
import { AsyncJobsDropdown, NotificationsDropdown } from "./shell/HeaderMenus";
import { HomePage } from "./pages/HomePage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { COPILOT_PANEL_ID, CopilotPanel } from "./pages/copilot/CopilotPanel";

// O @cx/ui não conhece o router: MenuItem.to é string. Os destinos do menu são rotas da árvore registrada em routes.tsx;
// o cast fica só neste ponto.
const renderLink: RenderLink = (to, children, active, className) => (
  // O PDV (/pdv, T3.1) é outra aplicação na mesma origem: carrega a página inteira (service worker e tela cheia).
  <Link
    reloadDocument={to === "/pdv"}
    to={to as "/"}
    className={className ?? `nav-link${active ? " active" : ""}`}
    aria-current={active ? "page" : undefined}
  >
    {children}
  </Link>
);

function Root() {
  const s = useSession();
  // Navegação pelo hook: o Root não depende do objeto router (sem import circular, FE1).
  const navigate = useNavigate();
  const path = useRouterState({ select: (r) => r.location.pathname });
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [copilotOpen, setCopilotOpen] = useState(false);
  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);
  const perms = s.me.permissions as Permissions;
  const copilot = can(perms, "copilot", "read");
  return (
    <AppShell
      menu={filterMenu(MENU, perms)}
      currentPath={path}
      renderLink={renderLink}
      userName={s.me.user.name}
      companies={s.me.companies}
      activeCompanyId={s.companyId}
      onCompanyChange={s.setCompanyId}
      onLogout={s.logout}
      onSearch={
        can(perms, "contacts", "read")
          ? (q) => navigate({ to: "/cadastros/contatos", search: { q } })
          : undefined
      }
      jobsSlot={can(perms, "jobs", "read") ? <AsyncJobsDropdown /> : undefined}
      notificationsSlot={<NotificationsDropdown />}
      copilot={
        copilot
          ? { open: copilotOpen, onToggle: () => setCopilotOpen((o) => !o), panelId: COPILOT_PANEL_ID }
          : undefined
      }
      aside={
        copilot && copilotOpen ? (
          <CopilotPanel route={path} onClose={() => setCopilotOpen(false)} />
        ) : undefined
      }
      theme={theme}
      onThemeChange={(t) => {
        saveTheme(t);
        setTheme(t);
      }}
    >
      <Outlet />
    </AppShell>
  );
}

export const rootRoute = createRootRoute({ component: Root, notFoundComponent: NotFoundPage });
export const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: HomePage });
